import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useCatalog } from "@/providers/CatalogProvider";
import {
  useAssessmentEntriesQuery,
  useStudentAssessmentsQuery,
  useStudentQuery,
} from "@/hooks/useStudents";
import { ReportExportPanel } from "@/components/report/ReportExportPanel";
import { ReportContextRequired } from "@/components/report/ReportContextRequired";
import { ReportSourceError } from "@/components/report/ReportSourceError";
import {
  ANALYSIS_FIELDS,
  DEVELOPMENT_FIELDS,
  IepPrintableDocument,
  PLAN_DOMAINS,
  PRIMARY_PLAN_CODES,
  SUMMARY_FIELDS,
  type IepDomainEntry,
  type PlanDomainCode,
} from "@/components/iep/IepPrintableDocument";
import { formatDate } from "@/utils/date";
import { getDomainByCode, type DomainCode } from "@/constants/domains";
import type { Assessment } from "@/types/assessment";
import type { ScaleEntry } from "@/types/scale";
import { VoiceInputButton } from "@/components/common/VoiceInputButton";
import { nanoid } from "nanoid";
import { fetchCapabilities, type IepContent } from "@/services/repositories/assessment-repository";
import { useIepDraft } from "@/hooks/useIepDraft";
import type { VersionedAutosaveStatus } from "@/hooks/useVersionedAutosave";
import {
  assessmentRecordLabel,
  completedAssessmentsForStudent,
  studentWorkspacePaths,
} from "@/utils/student-workspace";

const IEP_SECTIONS: Array<{ key: IepSection; label: string }> = [
  { key: "analysis", label: "评估结果分析" },
  { key: "development", label: "发展与课程需要" },
  { key: "summary", label: "综合摘要" },
  { key: "education", label: "教育目标" },
  { key: "support", label: "支持服务与家庭指导" },
];

type IepSection = "analysis" | "development" | "summary" | "education" | "support";

export function IEPReportPage(): JSX.Element {
  const { scaleLibrary } = useCatalog();
  const { studentId = "" } = useParams<{ studentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const studentQuery = useStudentQuery(studentId);
  const assessmentsQuery = useStudentAssessmentsQuery(studentId);

  const [pendingGoal, setPendingGoal] = useState<Partial<Record<PlanDomainCode, string>>>({});
  const [activeSection, setActiveSection] = useState<IepSection>("analysis");
  const [aiEnabled, setAiEnabled] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchCapabilities()
      .then((capabilities) => {
        if (!cancelled) {
          setAiEnabled(capabilities.iepAiEnabled);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAiEnabled(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const requestedAssessmentId = searchParams.get("assessmentId") ?? undefined;
  const completedAssessments = completedAssessmentsForStudent(
    assessmentsQuery.data ?? [],
    studentId,
  );
  const selectedSource = requestedAssessmentId
    ? completedAssessments.find((assessment) => assessment.id === requestedAssessmentId)
    : completedAssessments[0];
  const selectedAssessment =
    selectedSource?.integrityStatus === "verified" ? selectedSource : undefined;
  const selectedAssessmentId = selectedAssessment?.id;

  useEffect(() => {
    if (!requestedAssessmentId && completedAssessments[0]) {
      const next = new URLSearchParams(searchParams);
      next.set("assessmentId", completedAssessments[0].id);
      setSearchParams(next, { replace: true });
    }
  }, [completedAssessments, requestedAssessmentId, searchParams, setSearchParams]);

  function selectAssessment(assessmentId: string): void {
    const next = new URLSearchParams(searchParams);
    next.set("assessmentId", assessmentId);
    setSearchParams(next);
  }

  const entriesQuery = useAssessmentEntriesQuery(selectedAssessmentId);
  const entries = useMemo(() => entriesQuery.data ?? [], [entriesQuery.data]);

  const domainBuckets = useMemo(() => {
    const buckets = new Map<PlanDomainCode, IepDomainEntry[]>();
    PLAN_DOMAINS.forEach(({ code }) => buckets.set(code, []));
    entries.forEach((record) => {
      const entry = scaleLibrary.entryIndex.get(record.entryCode);
      if (!entry) {
        return;
      }
      const domainKey = toPlanDomainCode(entry.domainCode);
      buckets.get(domainKey)!.push({ entry, record });
    });
    return buckets;
  }, [entries, scaleLibrary.entryIndex]);

  const defaultIepContent = useMemo(() => buildDefaultIepContent(domainBuckets), [domainBuckets]);

  const {
    content: iepContent,
    updateContent: updateIepContent,
    flush: flushIepDraft,
    retry: retryIepDraft,
    reload: reloadIepDraft,
    generate: generateDraft,
    confirm: confirmDraft,
    saveStatus: iepSaveStatus,
    loadError: iepLoadError,
    formalVersion: iepVersion,
    versions: iepVersions,
    hasGeneratedDraft,
    isLoading: iepDraftLoading,
  } = useIepDraft({
    assessmentId: selectedAssessmentId,
    defaultContent: defaultIepContent,
    defaultsReady: !entriesQuery.isLoading && !entriesQuery.isError,
  });

  useEffect(() => {
    setPendingGoal({});
    setActiveSection("analysis");
    setFeedback(null);
  }, [selectedAssessmentId]);

  const handleAddGoal = (domainCode: PlanDomainCode, entryCode: string) => {
    if (!entryCode) {
      return;
    }
    updateIepContent((previous) => {
      const current = previous.educationPlan[domainCode].goalEntryCodes;
      if (current.includes(entryCode)) {
        return previous;
      }
      return {
        ...previous,
        educationPlan: {
          ...previous.educationPlan,
          [domainCode]: {
            ...previous.educationPlan[domainCode],
            goalEntryCodes: [...current, entryCode],
          },
        },
      };
    });
  };

  const handleRemoveGoal = (domainCode: PlanDomainCode, entryCode: string) => {
    updateIepContent((previous) => ({
      ...previous,
      educationPlan: {
        ...previous.educationPlan,
        [domainCode]: {
          ...previous.educationPlan[domainCode],
          goalEntryCodes: previous.educationPlan[domainCode].goalEntryCodes.filter(
            (code) => code !== entryCode,
          ),
        },
      },
    }));
  };

  const handleServiceFieldChange = (
    index: number,
    field: keyof IepContent["supportServices"][number],
    value: string,
  ) => {
    updateIepContent((previous) => ({
      ...previous,
      supportServices: previous.supportServices.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    }));
  };

  const handleAddSupportService = () => {
    updateIepContent((previous) => ({
      ...previous,
      supportServices: [...previous.supportServices, createSupportServiceRow()],
    }));
  };

  const handleRemoveSupportService = (index: number) => {
    updateIepContent((previous) => ({
      ...previous,
      supportServices:
        previous.supportServices.length === 1
          ? previous.supportServices
          : previous.supportServices.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const handleGenerateIep = async () => {
    if (!selectedAssessment) {
      return;
    }
    if (!aiEnabled) {
      setFeedback("当前环境未启用 AI，仍可手动填写并保存 IEP。");
      return;
    }
    if (
      hasGeneratedDraft &&
      iepContent &&
      hasMeaningfulIepContent(iepContent) &&
      !window.confirm("批量生成会替换当前服务器草稿内容，是否继续？")
    ) {
      return;
    }
    setGenerating(true);
    setFeedback(null);
    try {
      await generateDraft(nanoid());
      setFeedback("AI 内容已保存为服务器草稿。请逐区修改，确认无误后再正式确认。");
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "IEP 生成失败，请稍后重试");
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveIep = async () => {
    if (!selectedAssessment) {
      return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      const saved = await confirmDraft();
      setFeedback(`IEP 第 ${saved.version} 版已由评估师确认并保存。`);
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "IEP 保存失败，请稍后重试");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = async () => {
    setFeedback(null);
    const saved = await flushIepDraft({ forceCreate: true });
    if (!saved) {
      setFeedback("IEP 草稿未能保存到服务器，已阻止打印。请重试后再操作。");
      return;
    }
    window.print();
  };

  const sectionStates = useMemo(() => getIepSectionStates(iepContent), [iepContent]);

  return (
    <>
    <div className="space-y-6 print:hidden">
      <header className="overflow-hidden rounded-[1.75rem] border border-violet-200/80 bg-gradient-to-br from-white via-white to-accent-cool-soft shadow-[0_24px_55px_-38px_rgba(109,96,152,0.55)]">
        <div className="grid gap-6 p-6 lg:grid-cols-[minmax(0,1fr)_minmax(19rem,30rem)] lg:p-7">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-accent-cool">
              个别化教育计划
            </p>
            <h1 className="mt-3 text-2xl font-semibold tracking-tight text-slate-800 md:text-3xl">
              IEP 计划
            </h1>
            <p className="mt-2 text-sm text-slate-500">根据评估结果制定教育计划与学期目标。</p>
            {completedAssessments.length > 0 ? (
              <label className="mt-5 block max-w-xl text-sm font-semibold text-slate-700">
                评估依据
                <select
                  value={selectedSource?.id ?? ""}
                  onChange={(event) => selectAssessment(event.target.value)}
                  className="mt-2 min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-normal text-slate-700 shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                >
                  {completedAssessments.map((assessment) => (
                    <option key={assessment.id} value={assessment.id}>
                      {formatAssessmentOption(assessment)}
                      {assessment.integrityStatus === "verified" ? "" : " · 尚未校验"}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
          {selectedAssessment ? (
            <section className="rounded-2xl border border-white/90 bg-white/[0.85] p-5 shadow-[0_18px_42px_-34px_rgba(51,65,85,0.65)]">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold text-slate-800">IEP 草稿</h2>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    {aiEnabled
                      ? "生成整份草稿后逐区修改，再确认正式版本。"
                      : "当前环境未启用 AI，可手动填写。"}
                  </p>
                </div>
                <ReportExportPanel
                  disabled={iepDraftLoading}
                  onDownloadPdf={() => void handlePrint()}
                />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void handleGenerateIep()}
                  disabled={!aiEnabled || iepDraftLoading || generating || saving}
                  className="min-h-11 rounded-xl border border-accent-cool bg-white px-4 py-2 text-sm font-semibold text-accent-cool transition hover:bg-accent-cool-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-cool focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
                >
                  {generating ? "正在生成…" : "AI 生成 IEP"}
                </button>
                {hasGeneratedDraft ? (
                  <button
                    type="button"
                    onClick={() => void handleSaveIep()}
                    disabled={generating || saving}
                    className="min-h-11 rounded-xl bg-brand px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    {saving ? "正在保存…" : "确认并保存"}
                  </button>
                ) : null}
              </div>
              {feedback ? (
                <p className="mt-3 text-sm text-slate-700" role="status" aria-live="polite">
                  {feedback}
                </p>
              ) : null}
              <IepSaveIndicator
                status={iepSaveStatus}
                loadError={iepLoadError}
                onRetry={() => void retryIepDraft()}
                onCopy={() => void copyIepContent(iepContent)}
                onReload={() => void reloadIepDraft()}
              />
              {iepVersion > 0 ? (
                <p className="mt-2 text-xs text-slate-500">
                  正式版本：第 {iepVersion} 版
                  {iepVersions[0]?.confirmedByDisplayName
                    ? ` · ${iepVersions[0].confirmedByDisplayName}确认`
                    : ""}
                </p>
              ) : null}
            </section>
          ) : null}
        </div>
      </header>

      {assessmentsQuery.isLoading || studentQuery.isLoading ? (
        <section className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 shadow-sm">
          正在读取报告范围…
        </section>
      ) : null}

      {!assessmentsQuery.isLoading &&
      !studentQuery.isLoading &&
      requestedAssessmentId &&
      !selectedSource ? (
        <ReportSourceError
          studentId={studentId}
          title="无法打开这份 IEP"
          description="该评估不属于当前儿童或已经不可用。页面未读取或写入任何 IEP 草稿。"
          returnTo={studentWorkspacePaths.iep(studentId)}
          returnLabel="返回 IEP 计划"
        />
      ) : null}

      {!assessmentsQuery.isLoading &&
      !studentQuery.isLoading &&
      selectedSource &&
      !selectedAssessment ? (
        <ReportSourceError
          studentId={studentId}
          title="这份评估暂不能用于 IEP"
          description="最新或所选评估尚未通过完整性校验。系统不会自动改选另一份历史评估。"
          returnTo={studentWorkspacePaths.iep(studentId)}
          returnLabel="返回 IEP 计划"
        />
      ) : null}

      {!assessmentsQuery.isLoading &&
      !studentQuery.isLoading &&
      !selectedSource &&
      !requestedAssessmentId ? (
        <ReportContextRequired reportName="IEP 计划" studentId={studentId} />
      ) : null}

      {selectedAssessment ? (
        <div className="grid gap-5 lg:grid-cols-[16rem_minmax(0,1fr)] lg:items-start">
          <nav
            aria-label="IEP 编辑分区"
            className="grid gap-2 rounded-2xl border border-slate-200 bg-white/90 p-3 shadow-sm sm:grid-cols-2 lg:sticky lg:top-4 lg:max-h-[calc(100dvh-7rem)] lg:grid-cols-1 lg:overflow-y-auto"
          >
            <p className="px-2 pb-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 sm:col-span-2 lg:col-span-1">
              IEP 目录
            </p>
            {IEP_SECTIONS.map((section) => (
              <button
                key={section.key}
                type="button"
                onClick={() => setActiveSection(section.key)}
                aria-current={activeSection === section.key ? "step" : undefined}
                className={`rounded-xl border px-4 py-3 text-left transition ${
                  activeSection === section.key
                    ? "border-violet-200 bg-accent-cool-soft text-accent-cool shadow-sm"
                    : "border-transparent bg-slate-50 text-slate-600 hover:border-violet-200 hover:bg-accent-cool-soft/50"
                }`}
              >
                <span className="block text-sm font-semibold">{section.label}</span>
                <span className="mt-1 block text-xs">
                  {renderSectionState(sectionStates[section.key])}
                </span>
              </button>
            ))}
          </nav>
          <div className="min-w-0 space-y-5">
            {iepDraftLoading ? (
              <section className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
                正在读取服务器 IEP 草稿…
              </section>
            ) : null}
            {iepContent && activeSection === "analysis" ? (
              <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">各领域评估结果分析</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    内容可由评估师填写，或通过页面顶部按钮一次性生成草稿。
                  </p>
                </div>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  {ANALYSIS_FIELDS.map((field) => (
                    <TextAreaBlock
                      key={field.key}
                      label={field.label}
                      value={iepContent.analysis[field.key] ?? ""}
                      onChange={(value) =>
                        updateIepContent((previous) => ({
                          ...previous,
                          analysis: { ...previous.analysis, [field.key]: value },
                        }))
                      }
                    />
                  ))}
                </div>
              </section>
            ) : null}

            {iepContent && activeSection === "development" ? (
              <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-800">儿童发展现状</h2>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  {DEVELOPMENT_FIELDS.map((field) => (
                    <TextAreaBlock
                      key={field.key}
                      label={field.label}
                      value={iepContent.development[field.key] ?? ""}
                      onChange={(value) =>
                        updateIepContent((previous) => ({
                          ...previous,
                          development: { ...previous.development, [field.key]: value },
                        }))
                      }
                    />
                  ))}
                </div>
              </section>
            ) : null}

            {iepContent && activeSection === "summary" ? (
              <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-800">综合分析</h2>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  {SUMMARY_FIELDS.map((field) => (
                    <TextAreaBlock
                      key={field.key}
                      label={field.label}
                      value={iepContent.summary[field.key] ?? ""}
                      onChange={(value) =>
                        updateIepContent((previous) => ({
                          ...previous,
                          summary: { ...previous.summary, [field.key]: value },
                        }))
                      }
                    />
                  ))}
                </div>
              </section>
            ) : null}

            {iepContent && activeSection === "education" ? (
              <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <header>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-800">
                      融合教育方案 · 学期教育目标
                    </h2>
                    <p className="mt-1 text-sm text-slate-500">
                      按领域列出目前水平、学期目标与基准。默认为得分 1 的条目，可补充得分 0 的目标。
                    </p>
                  </div>
                </header>
                {PLAN_DOMAINS.map((domain) => {
                  const domainEntries = domainBuckets.get(domain.code) ?? [];
                  const currentLevel = domainEntries.filter((item) => item.record.score === 2);
                  const selectedCodes = iepContent.educationPlan[domain.code].goalEntryCodes;
                  const selectedDetails = selectedCodes
                    .map((code) => {
                      const entry = scaleLibrary.entryIndex.get(code);
                      return entry ? entry : null;
                    })
                    .filter((entry): entry is ScaleEntry => Boolean(entry));
                  const availableTargets = domainEntries.filter(
                    (item) =>
                      (item.record.score === 0 || item.record.score === 1) &&
                      !selectedCodes.includes(item.entry.code),
                  );

                  const domainName =
                    domain.code === "OTHER"
                      ? "跨领域 / 其他"
                      : getDomainByCode(domain.code as DomainCode).name;

                  return (
                    <article key={domain.code} className="rounded-lg border border-slate-200">
                      <div className="flex flex-col gap-1 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{domain.label}</p>
                          <p className="text-xs text-slate-500">{domainName}</p>
                        </div>
                        <p className="text-xs text-slate-500">
                          目前水平 {currentLevel.length} 项 · 学期目标 {selectedDetails.length} 项
                        </p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[1040px] table-fixed divide-y divide-slate-200 text-sm">
                          <colgroup>
                            <col className="w-[20%]" />
                            <col className="w-[23%]" />
                            <col className="w-[30%]" />
                            <col className="w-[27%]" />
                          </colgroup>
                          <thead className="bg-slate-50 text-slate-500">
                            <tr>
                              <th className="px-4 py-2 text-left font-medium">目前水平</th>
                              <th className="px-4 py-2 text-left font-medium">学期目标</th>
                              <th className="px-4 py-2 text-left font-medium">目标基准</th>
                              <th className="px-4 py-2 text-left font-medium">评价</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="bg-white">
                              <td className="align-top px-4 py-3">
                                {currentLevel.length === 0 ? (
                                  <span className="text-xs text-slate-400">
                                    尚无得分为 2 的条目
                                  </span>
                                ) : (
                                  <ul className="space-y-1 text-xs text-slate-600">
                                    {currentLevel.map((item) => (
                                      <li key={item.entry.code}>
                                        {item.entry.code} · {getEntryTitle(item.entry)}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </td>
                              <td className="align-top px-4 py-3">
                                <div className="space-y-2">
                                  {selectedDetails.length === 0 ? (
                                    <p className="text-xs text-slate-400">
                                      请选择需要关注的学期目标
                                    </p>
                                  ) : null}
                                  {selectedDetails.map((entry) => (
                                    <div
                                      key={entry.code}
                                      className="flex items-center justify-between rounded border border-slate-200 px-3 py-2 text-xs text-slate-600"
                                    >
                                      <span className="pr-3">
                                        {entry.code} · {getEntryTitle(entry)}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveGoal(domain.code, entry.code)}
                                        className="text-rose-600 hover:underline"
                                      >
                                        移除
                                      </button>
                                    </div>
                                  ))}
                                  {availableTargets.length > 0 ? (
                                    <select
                                      value={pendingGoal[domain.code] ?? ""}
                                      onChange={(event) => {
                                        const value = event.target.value;
                                        if (value) {
                                          handleAddGoal(domain.code, value);
                                          setPendingGoal((prev) => ({
                                            ...prev,
                                            [domain.code]: "",
                                          }));
                                        }
                                      }}
                                      className="w-full rounded border border-dashed border-slate-300 px-3 py-2 text-xs focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                    >
                                      <option value="">+ 添加目标（得分 0/1）</option>
                                      {availableTargets.map((item) => (
                                        <option key={item.entry.code} value={item.entry.code}>
                                          {item.entry.code} · {getEntryTitle(item.entry)}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <p className="text-xs text-slate-400">无可添加的条目</p>
                                  )}
                                </div>
                              </td>
                              <td className="align-top px-4 py-3">
                                {selectedDetails.length === 0 ? (
                                  <span className="text-xs text-slate-400">暂无目标基准</span>
                                ) : (
                                  <ul className="space-y-2 text-xs text-slate-600">
                                    {selectedDetails.map((entry) => (
                                      <li key={entry.code}>
                                        <p className="font-semibold">
                                          {entry.code} · {getEntryTitle(entry)}
                                        </p>
                                        <p className="mt-1 text-slate-500">{entry.standard}</p>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </td>
                              <td className="align-top px-4 py-3">
                                <div className="relative">
                                  <textarea
                                    value={iepContent.educationPlan[domain.code].evaluationNote}
                                    onChange={(event) =>
                                      updateIepContent((previous) => ({
                                        ...previous,
                                        educationPlan: {
                                          ...previous.educationPlan,
                                          [domain.code]: {
                                            ...previous.educationPlan[domain.code],
                                            evaluationNote: event.target.value,
                                          },
                                        },
                                      }))
                                    }
                                    rows={10}
                                    className="w-full rounded border border-slate-300 px-2 py-1 pr-12 text-xs text-slate-700 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 print:hidden"
                                    placeholder="记录过程评价或阶段性反馈"
                                  />
                                  <div
                                    className="hidden min-h-32 whitespace-pre-wrap break-words rounded border border-slate-300 px-2 py-2 text-xs leading-5 text-slate-700 print:block"
                                    data-print-evaluation={domain.code}
                                  >
                                    {iepContent.educationPlan[domain.code].evaluationNote ||
                                      "暂无过程评价或阶段性反馈"}
                                  </div>
                                  <VoiceInputButton
                                    size="sm"
                                    className="absolute bottom-2 right-2 print:hidden"
                                    initialValue={
                                      iepContent.educationPlan[domain.code].evaluationNote
                                    }
                                    onSave={(value) =>
                                      updateIepContent((previous) => ({
                                        ...previous,
                                        educationPlan: {
                                          ...previous.educationPlan,
                                          [domain.code]: {
                                            ...previous.educationPlan[domain.code],
                                            evaluationNote: value,
                                          },
                                        },
                                      }))
                                    }
                                  />
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </article>
                  );
                })}
              </section>
            ) : null}

            {iepContent && activeSection === "support" ? (
              <>
                <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                  <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-800">
                        特殊需要支持与服务安排
                      </h2>
                      <p className="text-sm text-slate-500">
                        结合儿童个别需求，记录具体的支持项目、服务频率与执行主体，开始/结束日期可与学期计划保持一致。
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddSupportService}
                      className="inline-flex items-center justify-center rounded-full border border-dashed border-brand px-3 py-1 text-xs font-semibold text-brand hover:bg-brand/5"
                    >
                      + 添加服务安排
                    </button>
                  </header>
                  <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50 text-slate-500">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">支持项目</th>
                          <th className="px-3 py-2 text-left font-medium">服务内容 / 形式</th>
                          <th className="px-3 py-2 text-left font-medium">负责人员 / 机构</th>
                          <th className="px-3 py-2 text-left font-medium">服务频率</th>
                          <th className="px-3 py-2 text-left font-medium">开始日期</th>
                          <th className="px-3 py-2 text-left font-medium">结束日期</th>
                          <th className="px-3 py-2 text-left font-medium">备注</th>
                          <th className="px-3 py-2 text-left font-medium">操作</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white text-slate-700">
                        {iepContent.supportServices.map((row, index) => (
                          <tr key={index}>
                            <td className="px-3 py-2 align-top">
                              <input
                                value={row.serviceName}
                                onChange={(event) =>
                                  handleServiceFieldChange(index, "serviceName", event.target.value)
                                }
                                className="h-20 w-full rounded border border-slate-300 px-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20 placeholder:text-slate-400"
                                placeholder="如个训、语言治疗等"
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <textarea
                                value={row.content}
                                onChange={(event) =>
                                  handleServiceFieldChange(index, "content", event.target.value)
                                }
                                rows={3}
                                className="h-20 w-full resize-none rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20 placeholder:text-slate-400"
                                placeholder="填写关键策略、开展形式等"
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <input
                                value={row.provider}
                                onChange={(event) =>
                                  handleServiceFieldChange(index, "provider", event.target.value)
                                }
                                className="h-20 w-full rounded border border-slate-300 px-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20 placeholder:text-slate-400"
                                placeholder="责任人或机构"
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <input
                                value={row.frequency}
                                onChange={(event) =>
                                  handleServiceFieldChange(index, "frequency", event.target.value)
                                }
                                className="h-20 w-full rounded border border-slate-300 px-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20 placeholder:text-slate-400"
                                placeholder="如每周 2 次 × 40 分钟"
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <input
                                type="date"
                                value={row.startDate}
                                onChange={(event) =>
                                  handleServiceFieldChange(index, "startDate", event.target.value)
                                }
                                className="h-20 w-full rounded border border-slate-300 px-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20 placeholder:text-slate-400"
                                placeholder="年 / 月 / 日"
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <input
                                type="date"
                                value={row.endDate}
                                onChange={(event) =>
                                  handleServiceFieldChange(index, "endDate", event.target.value)
                                }
                                className="h-20 w-full rounded border border-slate-300 px-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20 placeholder:text-slate-400"
                                placeholder="年 / 月 / 日"
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <textarea
                                value={row.note}
                                onChange={(event) =>
                                  handleServiceFieldChange(index, "note", event.target.value)
                                }
                                rows={3}
                                className="h-20 w-full resize-none rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20 placeholder:text-slate-400"
                                placeholder="补充说明"
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <button
                                type="button"
                                onClick={() => handleRemoveSupportService(index)}
                                className="text-xs text-rose-500 hover:underline disabled:text-slate-300"
                                disabled={iepContent.supportServices.length === 1}
                              >
                                删除
                              </button>
                              <p className="mt-1 text-xs text-slate-400">第 {index + 1} 行</p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                  <header>
                    <h2 className="text-lg font-semibold text-slate-800">家庭康复指导内容</h2>
                    <p className="mt-1 text-sm text-slate-500">
                      记录指导重点与家长支援资源，可结合语音输入与 AI
                      智能生成，帮助家长在家延伸练习。
                    </p>
                  </header>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <TextAreaBlock
                      label="指导内容"
                      value={iepContent.familyGuidance.guidance}
                      onChange={(value) =>
                        updateIepContent((previous) => ({
                          ...previous,
                          familyGuidance: { ...previous.familyGuidance, guidance: value },
                        }))
                      }
                    />
                    <TextAreaBlock
                      label="家长支援服务"
                      value={iepContent.familyGuidance.parentSupport}
                      onChange={(value) =>
                        updateIepContent((previous) => ({
                          ...previous,
                          familyGuidance: { ...previous.familyGuidance, parentSupport: value },
                        }))
                      }
                    />
                  </div>
                </section>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
    {iepContent && selectedAssessment ? (
      <IepPrintableDocument
        student={studentQuery.data}
        assessment={selectedAssessment}
        content={iepContent}
        domainBuckets={domainBuckets}
        entryIndex={scaleLibrary.entryIndex}
      />
    ) : null}
    </>
  );
}

function TextAreaBlock({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
      <span className="text-sm font-semibold text-slate-800">{label}</span>
      <div className="relative">
        <textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          rows={4}
          className="w-full rounded border border-slate-300 px-3 py-2 pr-12 text-sm text-slate-700 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          placeholder="请填写具体观察、结论或建议"
        />
        <VoiceInputButton
          size="sm"
          className="absolute bottom-2.5 right-2.5"
          initialValue={value}
          onSave={onChange}
        />
      </div>
    </div>
  );
}

function createSupportServiceRow(): IepContent["supportServices"][number] {
  return {
    serviceName: "",
    content: "",
    provider: "",
    frequency: "",
    startDate: "",
    endDate: "",
    note: "",
  };
}

function buildDefaultIepContent(domainBuckets: Map<PlanDomainCode, IepDomainEntry[]>): IepContent {
  return {
    analysis: buildFieldState(ANALYSIS_FIELDS),
    development: buildFieldState(DEVELOPMENT_FIELDS),
    summary: buildFieldState(SUMMARY_FIELDS),
    educationPlan: Object.fromEntries(
      PLAN_DOMAINS.map(({ code }) => [
        code,
        {
          goalEntryCodes: (domainBuckets.get(code) ?? [])
            .filter((item) => item.record.score === 1)
            .map((item) => item.entry.code),
          evaluationNote: "",
        },
      ]),
    ) as IepContent["educationPlan"],
    supportServices: [createSupportServiceRow()],
    familyGuidance: { guidance: "", parentSupport: "" },
  };
}

type IepSectionState = "empty" | "partial" | "complete";

function getIepSectionStates(content: IepContent | undefined): Record<IepSection, IepSectionState> {
  if (!content) {
    return {
      analysis: "empty",
      development: "empty",
      summary: "empty",
      education: "empty",
      support: "empty",
    };
  }
  const educationValues = Object.values(content.educationPlan).flatMap((plan) => [
    plan.goalEntryCodes.length > 0 ? "已选目标" : "",
    plan.evaluationNote,
  ]);
  const supportValues = [
    ...content.supportServices.flatMap((row) => Object.values(row)),
    ...Object.values(content.familyGuidance),
  ];
  return {
    analysis: classifySection(Object.values(content.analysis)),
    development: classifySection(Object.values(content.development)),
    summary: classifySection(Object.values(content.summary)),
    education: classifySection(educationValues),
    support: classifySection(supportValues),
  };
}

function classifySection(values: string[]): IepSectionState {
  const filled = values.filter((value) => value.trim().length > 0).length;
  if (filled === 0) return "empty";
  if (filled === values.length) return "complete";
  return "partial";
}

function renderSectionState(state: IepSectionState): string {
  if (state === "complete") return "完成";
  if (state === "partial") return "进行中";
  return "未填写";
}

function IepSaveIndicator({
  status,
  loadError,
  onRetry,
  onCopy,
  onReload,
}: {
  status: VersionedAutosaveStatus;
  loadError: unknown;
  onRetry: () => void;
  onCopy: () => void;
  onReload: () => void;
}): JSX.Element {
  const state = getIepSaveState(status);
  const message = loadError ? "读取服务器草稿失败，请稍后重试。" : state.label;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-3" aria-live="polite">
      {message ? (
        <p className={`text-sm ${loadError ? "text-red-600" : state.className}`}>{message}</p>
      ) : null}
      {status === "error" || status === "conflict" ? (
        <button
          type="button"
          onClick={onRetry}
          className="text-sm font-semibold text-brand hover:underline"
        >
          重试保存
        </button>
      ) : null}
      {status === "conflict" ? (
        <>
          <button
            type="button"
            onClick={onCopy}
            className="text-sm font-semibold text-brand hover:underline"
          >
            复制当前内容
          </button>
          <button
            type="button"
            onClick={onReload}
            className="text-sm font-semibold text-brand hover:underline"
          >
            重新载入服务器版本
          </button>
        </>
      ) : null}
    </div>
  );
}

async function copyIepContent(content: IepContent | undefined): Promise<void> {
  if (content && navigator.clipboard) {
    await navigator.clipboard.writeText(JSON.stringify(content, null, 2));
  }
}

function getIepSaveState(status: VersionedAutosaveStatus) {
  switch (status) {
    case "dirty":
      return { label: "有更改尚未保存", className: "text-amber-700" };
    case "saving":
      return { label: "正在保存…", className: "text-sky-700" };
    case "saved":
      return { label: "草稿已保存到服务器", className: "text-brand-dark" };
    case "error":
      return { label: "草稿保存失败，页面内容仍保留", className: "text-red-600" };
    case "conflict":
      return {
        label: "其他位置已更新，当前内容尚未保存",
        className: "text-red-600",
      };
    default:
      return { label: "", className: "text-slate-500" };
  }
}

function hasMeaningfulIepContent(content: IepContent): boolean {
  return (
    Object.values(content.analysis).some(Boolean) ||
    Object.values(content.development).some(Boolean) ||
    Object.values(content.summary).some(Boolean) ||
    Object.values(content.educationPlan).some(
      (plan) => plan.goalEntryCodes.length > 0 || Boolean(plan.evaluationNote),
    ) ||
    content.supportServices.some((row) => Object.values(row).some(Boolean)) ||
    Object.values(content.familyGuidance).some(Boolean)
  );
}

function buildFieldState(fields: readonly { key: string }[]): Record<string, string> {
  return fields.reduce<Record<string, string>>((acc, field) => {
    acc[field.key] = "";
    return acc;
  }, {});
}

function toPlanDomainCode(domainCode: DomainCode): PlanDomainCode {
  return PRIMARY_PLAN_CODES.includes(domainCode) ? (domainCode as PlanDomainCode) : "OTHER";
}

function getEntryTitle(entry: ScaleEntry): string {
  return entry.shortTermGoal?.title ?? entry.longTermGoal.title;
}

function formatAssessmentOption(assessment: Assessment): string {
  return `${formatDate(assessment.startedAt)} · ${assessmentRecordLabel()}`;
}
